/**
 * SharedSimulator — página `/simulator/shared/{id}` que abre uma simulação
 * guardada e é EXACTAMENTE equivalente ao simulador interno (`/simulator`):
 * mesmos widgets, mesmo layout (via LayoutBuilder / SimulatorPresets), mesmo
 * hook de cálculo (`useSimulatorCalculation`) → mesma paridade de valores.
 *
 * Diferenças vs SimulatorPage:
 * - Não tem histórico, tabs, comparador nem AnnualSimulator.
 * - Toolbar minimalista: Copiar Link / Gerar PDF / Atualizar (auth-only).
 * - Auto-save gated por `isAuthenticated`: candidatos podem simular à
 *   vontade, mas o snapshot em BD só é actualizado por utilizadores logados.
 * - Chama `POST /api/simulations/{id}/refresh` uma vez ao carregar para
 *   ressincronizar a listagem de Candidatos com os valores em BD.
 */
import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Calculator,
  Loader2,
  Save,
  Copy,
  FileText,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  BusinessTypeWidget,
  ConsultantWidget,
  RegimeWidget,
  ProposalWidget,
  ProjectWidget,
  CombinedBenefitsWidget,
  EmptyBenefitsWidget,
  EmptyPermBenefitsWidget,
  ScoreWidget,
  CompanyResultsWidget,
  EmployeeResultsWidget,
  PremiumWidget,
  BusinessSnapshotWidget,
  CashflowChartWidget,
} from "@/components/simulator/WidgetComponents";
import SimulatorPresets, {
  useEffectiveLayout,
} from "@/components/simulator/SimulatorPresets";
import { useSimulatorData } from "@/hooks/useSimulatorData";
import { useSimulatorCalculation } from "@/hooks/useSimulatorCalculation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce inline — evita re-fetch por cada tecla. Mesmo delay que o
// simulador principal (`useSimulatorForm.js:181`).
const useDebounce = (value, delay) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

// Cores de margem — replicam as usadas pelos widgets em SimulatorPage.
const marginColors = {
  danger: "#dc2626",
  warning: "#f59e0b",
  good: "#16a34a",
  neutral: "#64748b",
};
const snapshotColors = marginColors;

// Default form data — espelha `useSimulatorForm.defaultFormData` para que
// campos não populados pelo snapshot guardado não fiquem undefined.
const DEFAULT_FORM = {
  business_type: "outsourcing",
  regime_type: "contract",
  contract_type: "outsourcing",
  gross_value: "",
  value_type: "monthly",
  hours_per_month: 168,
  hours_per_day: 8,
  auto_hours_per_month: true,
  fix_fee_12_months: true,
  rv_dependency_level: "over_80",
  irs_type: "single",
  dependents: 0,
  irs_jovem: false,
  irs_jovem_year: 1,
  client_rate: "",
  client_rate_type: "monthly",
  billable_months: 11,
  payment_terms: 30,
  working_days_month: 21.7,
  payment_months: 11,
  include_meal_allowance: true,
  meal_allowance_daily: 10.46,
  meal_allowance_card: true,
  meal_allowance_months: 11,
  include_health_insurance: false,
  health_insurance_cost: 0,
  health_insurance_dependents: 0,
  health_insurance_months: 12,
  dependents_ages: [],
  dependents_costs: [],
  km_allowance: 0,
  km_allowance_months: 12,
  insurance_capitalization: 0,
  insurance_capitalization_months: 12,
  ticket_infancia: 0,
  ticket_infancia_months: 12,
  coverflex: 0,
  coverflex_months: 12,
  daily_rate: 0,
  selected_benefits: [],
  first_year_ss_exempt: false,
  include_agency_fee: false,
  include_manual_premium: false,
  manual_premium_value: 0,
  perm_candidate_salary: 0,
  perm_percentage_fee: 0,
  perm_fixed_fee: 0,
  vacation_days: 22,
  is_fix_fee: false,
  fix_fee_value: 0,
};

const SharedSimulator = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [consultantName, setConsultantName] = useState("");
  const [consultantBirthDate, setConsultantBirthDate] = useState("");
  const [consultantNif, setConsultantNif] = useState("");
  const [consultantEmail, setConsultantEmail] = useState("");
  const [consultantFunction, setConsultantFunction] = useState("");

  const debouncedFormData = useDebounce(formData, 500);

  // ============ Load + refresh saved simulation ============
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/simulations/${id}`, {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        setSimulation(response.data);
        setFormData((prev) => ({ ...prev, ...(response.data.simulation_data || {}) }));
        setConsultantName(response.data.name || "");
        setConsultantBirthDate(response.data.birth_date || "");
        setConsultantNif(
          response.data.nif || response.data.simulation_data?.consultant_nif || ""
        );
        setConsultantEmail(response.data.email || "");
        setConsultantFunction(response.data.simulation_data?.consultant_function || "");
      } catch (err) {
        if (!cancelled) setError("Simulação não encontrada ou link inválido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getAuthHeaders]);

  // Refresca o snapshot em BD com as taxas/settings actuais. Público — o
  // endpoint só usa `simulation_data` guardado, nunca inputs do cliente.
  useEffect(() => {
    if (loading) return;
    axios.post(`${API}/simulations/${id}/refresh`).catch(() => {});
  }, [loading, id]);

  // ============ Cálculo automático (mesmo hook que /simulator) ============
  const calc = useSimulatorCalculation(
    formData,
    debouncedFormData,
    getAuthHeaders,
    false, // reverseMode
    "", // desiredNetMonthly
    "", // debouncedDesiredNet
    user?.email || null,
    consultantName,
    consultantNif,
    consultantBirthDate,
    consultantFunction,
    consultantEmail
  );
  const { results, result, isCalculating } = calc;

  // ============ Side data (equipment, licenses, benefit settings, etc) ============
  // Endpoints requerem auth — para candidatos não autenticados o hook devolve
  // arrays vazios graciosamente, o que é suficiente para renderizar os widgets
  // essenciais (o ProjectWidget mostra selects vazios; o CombinedBenefits usa
  // defaults sensatos).
  const data = useSimulatorData(getAuthHeaders, setFormData);

  // ============ Layout efectivo (respeita LayoutBuilder do admin) ============
  const businessView =
    formData.business_type === "perm"
      ? "perm"
      : `outsourcing_${formData.regime_type || "contract"}`;
  const effLayout = useEffectiveLayout(getAuthHeaders, businessView);

  const isAdmin = user?.role === "admin";

  // ============ Auto-save (só para utilizadores autenticados) ============
  useEffect(() => {
    if (loading || !results || !id || !isAuthenticated) return;
    const t = setTimeout(async () => {
      try {
        await axios.put(
          `${API}/simulations/${id}`,
          {
            name: consultantName,
            birth_date: consultantBirthDate,
            notes: simulation?.notes || "",
            simulation_data: { ...formData, consultant_function: consultantFunction },
            results,
          },
          { headers: getAuthHeaders() }
        );
      } catch (e) {
        /* silent — botão "Atualizar" fica disponível para retry manual */
      }
    }, 900);
    return () => clearTimeout(t);
  }, [
    formData,
    results,
    consultantName,
    consultantBirthDate,
    id,
    simulation?.notes,
    isAuthenticated,
    loading,
    getAuthHeaders,
  ]);

  // ============ Handlers ============
  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error("Inicie sessão para guardar");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/simulations/${id}`,
        {
          name: consultantName,
          birth_date: consultantBirthDate,
          notes: simulation?.notes || "",
          simulation_data: { ...formData, consultant_function: consultantFunction },
          results,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Simulador atualizado!");
    } catch {
      toast.error("Erro ao guardar alterações");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!results) {
      toast.error("Aguarde o cálculo");
      return;
    }
    if (!isAuthenticated) {
      toast.error("Inicie sessão para gerar PDF");
      return;
    }
    const authHeaders = getAuthHeaders();
    try {
      let settings = { texts: {}, company: {} };
      try {
        const s = await axios.get(`${API}/proposal-settings`, { headers: authHeaders });
        settings = s.data || settings;
      } catch {
        /* fallback aos defaults do payload */
      }
      const texts = settings.texts || {};
      const company = settings.company || {};
      const payload = {
        consultant_name: consultantName || simulation?.name || "Candidato",
        consultant_nif: consultantNif || simulation?.nif || "",
        consultant_function: consultantFunction || "",
        simulation_data: { ...formData, consultant_function: consultantFunction },
        results,
        ...texts,
        cover_title: texts.cover_title || "Proposta Financeira",
        cover_subtitle: texts.cover_subtitle || "",
        greeting_text: texts.greeting_text || "",
        motivational_text: texts.motivational_text || "",
        annual_title: texts.annual_title || "Visão Anual Detalhada",
        annual_subtitle: texts.annual_subtitle || "",
        thanks_title: texts.thanks_title || "Obrigado!",
        thanks_text: texts.thanks_text || "",
        other_benefits_title: texts.other_benefits_title || "Benefícios",
        other_benefits_text: texts.other_benefits_text || "",
        footer_text: texts.footer_text || "",
        company_name: company.company_name || "Evolutt",
        company_slogan: company.company_slogan || "Unlocking What's Next",
        company_address: company.company_address || "",
        company_postal_code: company.company_postal_code || "",
        company_city: company.company_city || "",
        company_phone1: company.company_phone1 || "",
        company_phone2: company.company_phone2 || "",
        company_website: company.company_website || "",
        company_email: company.company_email || "",
        health_insurance_name: texts.health_insurance_name || "",
      };
      const r = await axios.post(`${API}/proposal/generate-pdf`, payload, {
        headers: authHeaders,
        responseType: "blob",
      });
      if (!r.data || r.data.size === 0) {
        toast.error("PDF vazio");
        return;
      }
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (consultantName || "proposta").replace(/\s+/g, "_");
      a.download = `Proposta_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("PDF gerado!");
    } catch (e) {
      const msg = e?.response?.status
        ? `Erro ${e.response.status}: ${e.response.statusText || "ao gerar PDF"}`
        : e?.message || "Erro ao gerar PDF";
      toast.error(msg);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copiado!");
  };

  // ============ Loading / error ============
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Erro</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link to="/">
              <Button>Voltar ao Início</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ Widgets map — MESMO que SimulatorPage ============
  const widgetContents = {
    form_business_type: (
      <BusinessTypeWidget
        formData={formData}
        setFormData={setFormData}
        allowedViews={effLayout.allowed_views}
      />
    ),
    form_consultant: (
      <ConsultantWidget
        consultantName={consultantName}
        setConsultantName={setConsultantName}
        consultantBirthDate={consultantBirthDate}
        setConsultantBirthDate={setConsultantBirthDate}
        consultantNif={consultantNif}
        setConsultantNif={setConsultantNif}
        consultantEmail={consultantEmail}
        setConsultantEmail={setConsultantEmail}
        consultantFunction={consultantFunction}
        setConsultantFunction={setConsultantFunction}
        jobFunctions={data.jobFunctions}
        formData={formData}
        setFormData={setFormData}
      />
    ),
    form_regime: (
      <RegimeWidget
        formData={formData}
        setFormData={setFormData}
        allowedViews={effLayout.allowed_views}
      />
    ),
    form_proposal: (
      <ProposalWidget
        formData={formData}
        setFormData={setFormData}
        isAdmin={isAdmin}
        result={result}
      />
    ),
    form_project: (
      <ProjectWidget
        formData={formData}
        setFormData={setFormData}
        equipment={data.equipment}
        licenses={data.licenses}
        clientSpecialFees={data.clientSpecialFees}
        clients={data.clients}
        results={results}
      />
    ),
    form_benefits:
      formData.business_type === "perm" ? (
        <EmptyPermBenefitsWidget formData={formData} setFormData={setFormData} />
      ) : formData.regime_type === "contract" ? (
        <CombinedBenefitsWidget
          formData={formData}
          setFormData={setFormData}
          customBenefits={data.customBenefits}
          benefitSettings={data.benefitSettings}
        />
      ) : (
        <EmptyBenefitsWidget
          regime={formData.regime_type}
          formData={formData}
          setFormData={setFormData}
        />
      ),
    results_score: <ScoreWidget result={result} results={results} />,
    results_snapshot: (
      <BusinessSnapshotWidget
        result={result}
        formData={formData}
        marginColors={marginColors}
        snapshotColors={snapshotColors}
      />
    ),
    results_company: (
      <CompanyResultsWidget
        result={result}
        results={results}
        formData={formData}
        marginColors={marginColors}
      />
    ),
    results_employee: <EmployeeResultsWidget result={result} formData={formData} />,
    results_premium: <PremiumWidget results={results} />,
    results_cashflow: <CashflowChartWidget result={result} formData={formData} />,
  };

  const hasCustomLayout =
    (effLayout.source === "user" || effLayout.source === "group") &&
    Array.isArray(effLayout.rows) &&
    effLayout.rows.length > 0;

  return (
    <div className="min-h-screen bg-background" data-testid="shared-simulator">
      {/* ============ Header ============ */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">
                Evolutt
              </span>
              <p className="text-xs text-muted-foreground">
                Simulação: {consultantName || id}
                {isCalculating && (
                  <span className="ml-2 text-brand-600">
                    <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                    a recalcular…
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/candidates")}
                data-testid="shared-back-btn"
                title="Voltar a Candidatos & Links"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={copyLink}
              data-testid="shared-copy-link-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar Link
            </Button>
            {isAuthenticated && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleGeneratePdf}
                data-testid="shared-generate-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Gerar PDF
              </Button>
            )}
            {isAuthenticated && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                data-testid="shared-update-btn"
                title="Guardar alterações e recalcular"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Atualizar Simulador
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* ============ Main (widgets idênticos ao frontoffice) ============ */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <SimulatorPresets
          preset="full"
          isAdmin={isAdmin}
          customLayout={
            hasCustomLayout
              ? { rows: effLayout.rows, custom_widgets: effLayout.custom_widgets }
              : null
          }
          contents={widgetContents}
        />
      </main>
    </div>
  );
};

export default SharedSimulator;
